import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Tooltip, styled, tooltipClasses } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useSelectedFieldContext } from "../../../../context/SelectedFieldProvider";
import SubTitle from "../../../Typography/SubTitle";
import SmallText from "../../../Typography/SmallText";
import { useWateringUsageContext } from "../../../../context/GeneralAgroProvider";

const TooltipStyled = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: "none",
    backgroundColor: "#fff",
    borderRadius: "5px",
    boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.25)",
  },
});

const WateringLimitsChip = styled("button")({
  width: "100%",
  minHeight: "40px",
  border: "none",
  borderRadius: "14px",
  backgroundColor: "#edf3ee",
  padding: "8px 14px",
  display: "inline-flex",
  alignItems: "center",
  gap: "10px",
  cursor: "pointer",
  transition: "background-color 0.15s ease",
  "&:hover": {
    backgroundColor: "#e4ede6",
  },
});

const WateringLimitsLabel = styled("span")({
  fontStyle: "normal",
  fontWeight: 400,
  fontSize: "12px",
  lineHeight: "16px",
  letterSpacing: "0.5px",
  verticalAlign: "middle",
  color: "#1f2d24",
  textAlign: "left",
});

const WateringLimitsIcon = styled(InfoOutlinedIcon)({
  color: "#28a745",
  fontSize: "22px",
  flexShrink: 0,
});

const WateringLimitsTooltip = ({ limitsText, onClose }) => {
  const { t, i18n } = useTranslation();
  const isEn = i18n.language.startsWith("en");

  const newRef = useRef(null);

  const handleOutsideClick = useCallback(
    (e) => {
      if (newRef.current && !newRef.current.contains(e.target)) {
        onClose?.();
      }
    },
    [onClose]
  );

  useEffect(() => {
    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [handleOutsideClick]);

  const limitsTextTranslated = isEn
    ? limitsText?.descriptionEn
    : limitsText?.descriptionGr;
  const isEmptyLimits = !limitsTextTranslated?.trim();
  const displayText = limitsTextTranslated?.trim()
    ? limitsTextTranslated
    : t("ActionModal.WateringLimitsEmpty");

  return (
    <div
      ref={newRef}
      style={{
        width: "300px",
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        padding: "8px",
        minHeight: isEmptyLimits ? "120px" : "auto",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <SubTitle text={t("ActionModal.WateringLimits")} />
        <CloseIcon sx={{ cursor: "pointer", fill: "#000" }} onClick={onClose} />
      </div>
      <div
        style={{
          width: "100%",
          textAlign: isEmptyLimits ? "center" : "left",
          display: "flex",
          flex: 1,
          alignItems: isEmptyLimits ? "center" : "flex-start",
          justifyContent: isEmptyLimits ? "center" : "flex-start",
        }}
      >
        <SmallText text={displayText} />
      </div>
    </div>
  );
};

const WateringLimits = () => {
  const { t } = useTranslation();
  const [showLimits, setShowLimits] = useState(false);
  const [limits, setLimits] = useState();
  const { getWateringLimits } = useWateringUsageContext();
  const { selectedField: field } = useSelectedFieldContext();

  useEffect(() => {
    const fieldCropId = field?.varietyRead?.cropId;
    if (fieldCropId) {
      getWateringLimits(fieldCropId).then((lim) => {
        setLimits(lim);
      });
    }
  }, [field, getWateringLimits]);

  return (
    <TooltipStyled
      open={showLimits}
      title={
        <WateringLimitsTooltip
          limitsText={limits}
          onClose={() => setShowLimits(false)}
        />
      }
      placement="bottom-start"
    >
      <WateringLimitsChip type="button" onClick={() => setShowLimits((prev) => !prev)}>
        <WateringLimitsIcon />
        <WateringLimitsLabel>{t("ActionModal.WateringLimits")}</WateringLimitsLabel>
      </WateringLimitsChip>
    </TooltipStyled>
  );
};

export default WateringLimits;
